import React, { useState, useRef, useEffect, useMemo } from "react";
import { Controller } from "react-hook-form";
import { useCustomers } from "../../queries/customerQueries";

// ─── Searchable Dropdown for Party Name / Importer with Server Search ────────
function PartySearchDropdown({
    value,
    onChange,
    placeholder = "Search customer...",
    id = "pl-partyName",
    emptyMessage = "No customers found"
}) {
    const [query, setQuery] = useState(value || "");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    // Keep input in sync when form value changes externally (e.g. edit mode restore)
    useEffect(() => {
        setQuery(value || "");
    }, [value]);

    // Debounce search query to trigger server search query with `search` param
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(query);
        }, 300);
        return () => clearTimeout(handler);
    }, [query]);

    // Server-side search call using query params: page=1&pageSize=20&search=debouncedSearch
    const { data: apiCustomers, isLoading } = useCustomers(1, debouncedSearch);

    const customerList = useMemo(() => {
        const raw = apiCustomers?.data || apiCustomers?.items || (Array.isArray(apiCustomers) ? apiCustomers : []);
        return raw || [];
    }, [apiCustomers]);

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleSelect = (customer) => {
        const name = customer?.customerName || "";
        setQuery(name);
        setOpen(false);
        onChange(name, customer);
    };

    const handleClear = () => {
        setQuery("");
        setOpen(false);
        onChange("", null);
    };

    return (
        <div ref={wrapperRef} style={{ position: "relative" }}>
            <div className="d-flex align-items-center" style={{ position: "relative" }}>
                <input
                    id={id}
                    type="text"
                    className="form-control pe-4"
                    placeholder={placeholder}
                    value={query}
                    autoComplete="off"
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                />
                {/* Clear button */}
                {query && (
                    <button
                        type="button"
                        onClick={handleClear}
                        style={{
                            position: "absolute",
                            right: "8px",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#6c757d",
                            fontSize: "14px",
                            lineHeight: 1,
                            padding: "0",
                        }}
                        title="Clear"
                    >
                        ✕
                    </button>
                )}
            </div>

            {/* Dropdown list */}
            {open && (
                <ul
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 1050,
                        maxHeight: "220px",
                        overflowY: "auto",
                        background: "#fff",
                        border: "1px solid #ced4da",
                        borderTop: "none",
                        borderRadius: "0 0 6px 6px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        margin: 0,
                        padding: 0,
                        listStyle: "none",
                    }}
                >
                    {isLoading ? (
                        <li style={{ padding: "8px 12px", fontSize: "13px", color: "#6c757d" }}>
                            Searching...
                        </li>
                    ) : customerList.length > 0 ? (
                        customerList.map((c, i) => (
                            <li
                                key={c?.id || c?.customerId || i}
                                onMouseDown={() => handleSelect(c)}
                                style={{
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    borderBottom: "1px solid #f0f0f0",
                                    transition: "background 0.15s",
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f6ff")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                            >
                                {c?.customerName}
                            </li>
                        ))
                    ) : (
                        <li style={{ padding: "8px 12px", fontSize: "13px", color: "#6c757d" }}>
                            {emptyMessage}
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
}


const Step1Details = ({
    control,
    customerNameList,
    updateCustomerFields,
    goToNextStep,
    watch,
    isLoading,
    updateLoading,
}) => {
    const modifyTotalCBM = watch("modifyTotalCBM");

    const labelStyle = {
        fontSize: "11.5px",
        fontWeight: "600",
        letterSpacing: "0.5px",
        color: "#1F2937",
    };

    return (
        <div className="card shadow-sm border rounded-top-0 rounded-bottom-3 p-3 p-md-4 d-flex flex-column justify-content-between flex-grow-1">
            {/* Fields Container */}
            <div>
                {/* ── Row 1 : FILE ID | DATE ── */}
                <div className="row g-3 align-items-center mb-3">
                    <label htmlFor="pl-fileId" className="col-4 col-md-2 col-form-label fw-bold text-uppercase" style={labelStyle}>
                        File ID :
                    </label>
                    <div className="col-8 col-md-4">
                        <Controller control={control} name="fileId"
                            render={({ field }) => (
                                <input
                                    {...field}
                                    id="pl-fileId"
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter file ID"
                                />
                            )}
                        />
                    </div>
                    <label htmlFor="pl-date" className="col-4 col-md-2 col-form-label fw-bold text-uppercase" style={labelStyle}>
                        Date :
                    </label>
                    <div className="col-8 col-md-4">
                        <Controller control={control} name="plDate"
                            render={({ field }) => (
                                <input
                                    id="pl-date"
                                    type="date"
                                    className="form-control"
                                    value={field.value ? field.value.toString().slice(0, 10) : ''}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        field.onChange(v ? `${v}T00:00:00.000Z` : null);
                                    }}
                                />
                            )}
                        />
                    </div>
                </div>

                {/* ── Row 2 : AGENT NAME | PARTY NAME ── */}
                <div className="row g-3 align-items-center mb-3">
                    <label htmlFor="pl-agentName" className="col-4 col-md-2 col-form-label fw-bold text-uppercase" style={labelStyle}>
                        Agent Name :
                    </label>
                    <div className="col-8 col-md-4">
                        <Controller control={control} name="agentName"
                            render={({ field }) => (
                                <input
                                    {...field}
                                    id="pl-agentName"
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter agent name"
                                />
                            )}
                        />
                    </div>
                    <label htmlFor="pl-partyName" className="col-4 col-md-2 col-form-label fw-bold text-uppercase" style={labelStyle}>
                        Party Name :
                    </label>
                    <div className="col-8 col-md-4">
                        <Controller
                            control={control}
                            name="partyName"
                            defaultValue=""
                            render={({ field }) => (
                                <PartySearchDropdown
                                    id="pl-partyName"
                                    placeholder="Search customer..."
                                    emptyMessage="No customers found"
                                    value={field.value}
                                    onChange={(val, matchedObj) => {
                                        field.onChange(val);
                                        updateCustomerFields(matchedObj);
                                    }}
                                />
                            )}
                        />
                    </div>
                </div>

                {/* ── Row 3 : PKL | CBM ── */}
                <div className="row g-3 align-items-center mb-3">
                    <label htmlFor="pl-pkl" className="col-4 col-md-2 col-form-label fw-bold text-uppercase" style={labelStyle}>
                        PKL :
                    </label>
                    <div className="col-8 col-md-4">
                        <Controller control={control} name="pkl"
                            render={({ field }) => (
                                <input
                                    {...field}
                                    id="pl-pkl"
                                    type="number"
                                    step="any"
                                    className="form-control"
                                    placeholder="0"
                                />
                            )}
                        />
                    </div>
                    <label htmlFor="pl-cbm" className="col-4 col-md-2 col-form-label fw-bold text-uppercase" style={labelStyle}>
                        CBM :
                    </label>
                    <div className="col-8 col-md-4">
                        <Controller control={control} name="cbm"
                            render={({ field }) => (
                                <input
                                    {...field}
                                    id="pl-cbm"
                                    type="number"
                                    step="any"
                                    className="form-control"
                                    placeholder="0.000"
                                />
                            )}
                        />
                    </div>
                </div>

                {/* ── Row 4 : COMM | LOAD & MTS AMT ── */}
                <div className="row g-3 align-items-center mb-3">
                    <label htmlFor="pl-comm" className="col-4 col-md-2 col-form-label fw-bold text-uppercase" style={labelStyle}>
                        Comm :
                    </label>
                    <div className="col-8 col-md-4">
                        <Controller control={control} name="comm"
                            render={({ field }) => (
                                <input
                                    {...field}
                                    id="pl-comm"
                                    type="number"
                                    step="any"
                                    className="form-control bg-light"
                                    placeholder="Auto-calculated"
                                    readOnly
                                />
                            )}
                        />
                    </div>
                    <label htmlFor="pl-load" className="col-4 col-md-2 col-form-label fw-bold text-uppercase" style={labelStyle}>
                        Load :
                    </label>
                    <div className="col-8 col-md-4">
                        <div className="d-flex flex-wrap flex-sm-nowrap align-items-center gap-2">
                            <div className="flex-grow-1" style={{ minWidth: "70px" }}>
                                <Controller control={control} name="load"
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            id="pl-load"
                                            type="number"
                                            step="any"
                                            className="form-control"
                                            placeholder="0.00"
                                        />
                                    )}
                                />
                            </div>
                            <label htmlFor="pl-mtsNewValue" className="fw-bold text-uppercase mb-0 text-nowrap" style={{ ...labelStyle, fontSize: "11px" }}>
                                MTS AMT:
                            </label>
                            <div style={{ minWidth: "75px", flex: "1 1 75px" }}>
                                <Controller control={control} name="mtsNewValue"
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            id="pl-mtsNewValue"
                                            type="number"
                                            step="any"
                                            className={`form-control ${modifyTotalCBM ? "bg-light" : ""}`}
                                            placeholder="0.00"
                                            readOnly={!!modifyTotalCBM}
                                            title={modifyTotalCBM ? "Locked — Modify Girth applied" : ""}
                                            style={{ cursor: modifyTotalCBM ? "not-allowed" : "auto" }}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Row 5 : RECEIVED | PAID AMOUNT ── */}
                <div className="row g-3 align-items-center mb-3">
                    <label htmlFor="pl-received" className="col-4 col-md-2 col-form-label fw-bold text-uppercase" style={labelStyle}>
                        Received :
                    </label>
                    <div className="col-8 col-md-4">
                        <Controller control={control} name="received"
                            render={({ field }) => (
                                <input
                                    {...field}
                                    id="pl-received"
                                    type="number"
                                    step="any"
                                    className="form-control"
                                    placeholder="0.00"
                                />
                            )}
                        />
                    </div>
                    <label htmlFor="pl-paidAmount" className="col-4 col-md-2 col-form-label fw-bold text-uppercase" style={labelStyle}>
                        Paid Amount :
                    </label>
                    <div className="col-8 col-md-4">
                        <Controller control={control} name="paidAmount"
                            render={({ field }) => (
                                <input
                                    {...field}
                                    id="pl-paidAmount"
                                    type="number"
                                    step="any"
                                    className="form-control"
                                    placeholder="0.00"
                                />
                            )}
                        />
                    </div>
                </div>

                {/* ── Row 6 : BALANCE AMOUNT | RATE ── */}
                <div className="row g-3 align-items-center mb-3">
                    <label htmlFor="pl-balanceAmount" className="col-4 col-md-2 col-form-label fw-bold text-uppercase" style={labelStyle}>
                        Balance Amount :
                    </label>
                    <div className="col-8 col-md-4">
                        <Controller control={control} name="balanceAmount"
                            render={({ field }) => (
                                <input
                                    {...field}
                                    id="pl-balanceAmount"
                                    type="number"
                                    step="any"
                                    className="form-control bg-light"
                                    placeholder="Auto-calculated"
                                    readOnly
                                />
                            )}
                        />
                    </div>
                    <label htmlFor="pl-rate" className="col-4 col-md-2 col-form-label fw-bold text-uppercase" style={labelStyle}>
                        Rate :
                    </label>
                    <div className="col-8 col-md-4">
                        <Controller control={control} name="rate"
                            render={({ field }) => (
                                <input
                                    {...field}
                                    id="pl-rate"
                                    type="number"
                                    step="any"
                                    className="form-control"
                                    placeholder="0.00"
                                />
                            )}
                        />
                    </div>
                </div>

                {/* ── Row 7 : IMPORTER ── */}
                <div className="row g-3 align-items-center mb-3">
                    <label htmlFor="pl-importer" className="col-4 col-md-2 col-form-label fw-bold text-uppercase" style={labelStyle}>
                        Importer :
                    </label>
                    <div className="col-8 col-md-4">
                        <Controller
                            control={control}
                            name="importer"
                            defaultValue=""
                            render={({ field }) => (
                                <PartySearchDropdown
                                    id="pl-importer"
                                    placeholder="Search importer..."
                                    emptyMessage="No importers found"
                                    value={field.value}
                                    onChange={(val) => field.onChange(val)}
                                />
                            )}
                        />
                    </div>
                </div>

                {/* Hidden MTS field (needed for calculation) */}
                <div style={{ display: "none" }}>
                    <Controller control={control} name="mts"
                        render={({ field }) => <input {...field} type="number" step="any" />}
                    />
                </div>
            </div>

            {/* ── Navigation Buttons ── */}
            <div className="text-center pt-2 pb-1 d-flex flex-wrap justify-content-center gap-2 gap-sm-3">
                <button
                    type="button"
                    id="pl-step1-next-btn"
                    className="btn px-5 py-2 fw-semibold text-white gtss-bg-primary border-0"
                    style={{ borderRadius: "20px" }}
                    onClick={goToNextStep}
                >
                    Next
                </button>
                <button
                    type="submit"
                    id="pl-step1-submit-btn"
                    className="btn px-5 py-2 fw-semibold text-white gtss-bg-accent border-0"
                    style={{ borderRadius: "20px" }}
                    disabled={isLoading || updateLoading}
                >
                    {isLoading || updateLoading ? "Saving..." : "Submit"}
                </button>
            </div>
        </div>
    );
};

export default Step1Details;
